import React from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/Card/Card";
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ChartTooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { useTheme } from "@/context/ThemeContext";
import {
  formatCurrency,
  formatPercentage,
} from "@/modules/stochastic/features/Reports/SCDashboard/utils/chart";
import { RevenueByInvoiceAgeDataItem } from "@/modules/stochastic/features/Reports/SCDashboard/api/fetchStochasticDashboardData/types";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Info } from "lucide-react";

type RevenueByInvoiceAgeChartProps = {
  chartId?: string;
  initialData: RevenueByInvoiceAgeDataItem[];
};

export default function RevenueByInvoiceAgeChart({
  initialData,
}: RevenueByInvoiceAgeChartProps) {
  const { theme } = useTheme();

  return (
    <Card className="w-full">
      <CardHeader>
        <div className="flex items-center gap-2">
          <CardTitle className="text-fontColor dark:text-light">
            Revenue by Invoice Age
          </CardTitle>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <Info className="h-4 w-4 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 cursor-help" />
              </TooltipTrigger>
              <TooltipContent className="max-w-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100 shadow-lg">
                <p>
                  Reveals whether your business relies on recent work, has
                  strong repeat business from past customers, or maintains a
                  healthy balance of both.
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Assess business health through revenue timing patterns
        </p>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer height={400} width="100%">
          <ComposedChart
            data={initialData}
            margin={{
              top: 5,
              right: 30,
              left: 45,
              bottom: 5,
            }}
          >
            <CartesianGrid
              stroke={theme === "dark" ? "#374151" : "#e5e7eb"}
              strokeDasharray="3 3"
            />
            <XAxis
              dataKey="salesPeriod"
              stroke={theme === "dark" ? "#9CA3AF" : "#4B5563"}
            />
            <YAxis
              domain={[0, "dataMax"]}
              stroke={theme === "dark" ? "#9CA3AF" : "#4B5563"}
              tickFormatter={formatCurrency}
              yAxisId="left"
            />
            <YAxis
              domain={[0, 100]}
              orientation="right"
              stroke={theme === "dark" ? "#9CA3AF" : "#4B5563"}
              tickFormatter={formatPercentage}
              yAxisId="right"
            />
            <ChartTooltip
              contentStyle={{
                backgroundColor: theme === "dark" ? "#1F2937" : "#FFFFFF",
                borderColor: theme === "dark" ? "#374151" : "#E5E7EB",
                color: theme === "dark" ? "#F3F4F6" : "#1F2937",
              }}
              formatter={(value, name) => {
                if (name === "Total Sales")
                  return formatCurrency(value as number);
                if (name === "Sales %")
                  return formatPercentage(value as number);
                return value;
              }}
            />
            <Legend
              wrapperStyle={{
                color: theme === "dark" ? "#F3F4F6" : "#1F2937",
              }}
            />
            <Bar
              dataKey="totalSales"
              fill={theme === "dark" ? "#60A5FA" : "#2E8BC0"}
              name="Total Sales"
              yAxisId="left"
            />
            <Line
              dataKey="salesPercentage"
              dot={{ fill: theme === "dark" ? "#F97316" : "#FF7300", r: 4 }}
              name="Sales %"
              stroke={theme === "dark" ? "#F97316" : "#FF7300"}
              strokeWidth={2}
              type="monotone"
              yAxisId="right"
            />
          </ComposedChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
